import { router } from '@inertiajs/react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import * as React from 'react';
import DriveSyncLayout from '../../layouts/DriveSyncLayout';
import DataStreamsHeader from './DataStreamsHeader';

const UploadArea = styled(Paper)(({ theme, isDragActive }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: isDragActive ? theme.palette.action.hover : 'transparent',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  }
}));

const HiddenInput = styled('input')({
  display: 'none',
});

export default function New({ errors = {} }) {
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [isDragActive, setIsDragActive] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [formErrors, setFormErrors] = React.useState(errors);

  // Sync server errors with local state when props change
  React.useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setLoading(false);
    }
  }, [errors]);

  const fileInputRef = React.useRef(null);

  const acceptedTypes = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv'
  ];

  const getValidErrors = () => {
    return Object.values(formErrors).filter(error => error && error.trim());
  };

  const isValidFileType = (file) => {
    return acceptedTypes.includes(file.type) ||
      file.name.toLowerCase().endsWith('.csv') ||
      file.name.toLowerCase().endsWith('.xlsx') ||
      file.name.toLowerCase().endsWith('.xls');
  };

  const handleFileSelect = (file) => {
    setFormErrors({});

    if (!file) return;

    if (!isValidFileType(file)) {
      setFormErrors({ file: 'Por favor, selecione um arquivo Excel (.xlsx, .xls) ou CSV (.csv)' });
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      setFormErrors({ file: 'O arquivo deve ter no máximo 10MB' });
      return;
    }

    setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isDragActive) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragActive(false);
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setFormErrors({});

    try {
      const formData = new FormData();
      formData.append('data_stream[spreadsheet]', selectedFile);

      // Using router.post with FormData
      router.post('/data_streams', formData, {
        preserveScroll: true,
        onSuccess: () => {
          // Redirect será feito pelo controller
        },
        onError: (errors) => {
          setFormErrors(errors);
        },
        onFinish: () => {
          setLoading(false);
        }
      });
    } catch (err) {
      setFormErrors({ general: 'Erro inesperado ao enviar arquivo' });
      setLoading(false);
    }
  };

  const handleBack = () => {
    router.get('/data_streams');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <DriveSyncLayout>
      <DataStreamsHeader />

      <Box sx={{
        width: '100%',
        maxWidth: { sm: '100%', md: '800px' },
        mx: 'auto',
        mt: 2
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Importar Planilha
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Faça upload de um arquivo Excel (.xlsx, .xls) ou CSV (.csv) para importar dados.
        </Typography>

        {/* Upload Area */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <UploadArea
              isDragActive={isDragActive}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudUploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {isDragActive ? 'Solte o arquivo aqui' : 'Arraste um arquivo ou clique para selecionar'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Formatos aceitos: Excel (.xlsx, .xls) ou CSV (.csv)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tamanho máximo: 10MB
              </Typography>

              <HiddenInput
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleInputChange}
              />
            </UploadArea>
          </CardContent>
        </Card>

        {/* File Preview */}
        {selectedFile && (
          <Card elevation={1} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Arquivo Selecionado
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AttachFileIcon color="primary" />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(selectedFile.size)} • {selectedFile.type || 'Arquivo'}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSelectedFile(null)}
                  disabled={loading}
                >
                  Remover
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Error Alert */}
        {getValidErrors().length > 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2" component="div">
              <strong>Foram encontrados {getValidErrors().length} erro(s):</strong>
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              {getValidErrors().map((error, index) => (
                <Box component="li" key={index}>
                  <Typography variant="body2">{error}</Typography>
                </Box>
              ))}
            </Box>
          </Alert>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="outlined"
            onClick={handleUpload}
            disabled={!selectedFile || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          >
            {loading ? 'Analisando...' : 'Importar Planilha'}
          </Button>
        </Box>
      </Box>
    </DriveSyncLayout>
  );
} 